import classNames from "classnames"
import React from "react"

import { extractFromTag, getThemeColor } from "./utils"
import GatsbyStoryblokImage from "../../../elements/V2/GatsbyStoryblokImage"
import AccordionArrowIcon from "../../../elements/V2/Icons/AccordionArrowIcon"
import Tooltip from "../../../elements/V2/Tooltip"
import Typography from "../../../elements/V2/Typography"

import { TableRegex } from "@constants/tables"
import { Color } from "@constants/V2/color"
import { getAccentColor } from "@utils/V2/color"

const CELL_TEXT_CLASSSES =
  "[display:-webkit-box] [-webkit-box-orient:vertical] text-start"

type TableHeaderProps = {
  columnHeading: string
  columnIndex: number
  displayIndex: number
  isOpen: boolean
  hasAvailableColumns: boolean
  columnImage?: string
  theme: "light" | "dark"
  backgroundColor: Color
  maxMobileColumns: number
  maxDesktopColumns: number
  onToggleDropdown: () => void
}

const TableHeader = ({
  columnHeading,
  columnIndex,
  displayIndex,
  isOpen,
  hasAvailableColumns,
  columnImage,
  theme,
  backgroundColor,
  maxMobileColumns,
  maxDesktopColumns,
  onToggleDropdown,
}: TableHeaderProps) => {
  const isProduct = columnHeading.match(TableRegex.PRODUCT)

  return (
    <div
      key={`${columnHeading.replace(" ", "-")}-${columnIndex}`}
      className={classNames({
        "cursor-pointer": hasAvailableColumns,
        "hidden sm:block":
          displayIndex >= maxMobileColumns && displayIndex < maxDesktopColumns,
        hidden: displayIndex >= maxDesktopColumns,
      })}
      onClick={() => {
        if (hasAvailableColumns) {
          onToggleDropdown()
        }
      }}
    >
      <div
        className={classNames(
          "flex items-center gap-20 rounded-10 border-charcoal/10 py-4",
          {
            "sm:border": hasAvailableColumns,
            "h-40": !columnImage,
            "h-full min-h-[55px] sm:min-h-[72px]": columnImage,
            "rounded-b-none !border-b-0": isOpen && hasAvailableColumns,
            "shadow-lg": isOpen,
          }
        )}
      >
        {columnImage && (
          <div className="ml-[14px] aspect-square h-32 w-32 sm:h-64 sm:w-64">
            <GatsbyStoryblokImage
              aspectRatio="fullWidth"
              image={extractFromTag(columnImage, "image")}
              alt={columnHeading}
              className="h-32 w-32 sm:h-64 sm:w-64"
              testId="comparison-table-image"
              imageObjectFit="contain"
            />
          </div>
        )}
        <div className="flex flex-col gap-[3px]">
          <Tooltip
            text={columnHeading}
            backgroundColor={getThemeColor(theme)}
            showTooltipCondition="hoverContentEllipsed"
          >
            <Typography
              font="grotesk"
              weight="medium"
              color={getThemeColor(theme)}
              size="body-lg"
              text={
                isProduct
                  ? isProduct[1]
                  : extractFromTag(columnHeading, "title")
              }
              className={CELL_TEXT_CLASSSES}
            />
          </Tooltip>
          {isProduct && isProduct[2] && (
            <Tooltip
              text={columnHeading}
              backgroundColor={getThemeColor(theme)}
              showTooltipCondition="hoverContentEllipsed"
            >
              <Typography
                font="grotesk"
                weight="book"
                color={getThemeColor(theme)}
                size="body-sm"
                text={isProduct[2]}
                className={classNames(
                  "!hidden sm:![display:-webkit-box]",
                  CELL_TEXT_CLASSSES
                )}
              />
            </Tooltip>
          )}
        </div>
        {hasAvailableColumns && (
          <div className="ml-auto pr-8">
            <AccordionArrowIcon
              open={isOpen}
              color={getAccentColor(
                theme === "dark"
                  ? backgroundColor === Color.Black
                    ? Color.Black
                    : Color.Charcoal
                  : Color.White
              )}
            />
          </div>
        )}
      </div>
    </div>
  )
}

export default TableHeader
